import { useQuery } from "@tanstack/react-query";
import { SEVERITY_ORDER, type SpendBucket } from "@reviewer73/shared";
import { AlertTriangle, Banknote, Eye, GitPullRequestArrow, Timer, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { api } from "../api/client.js";
import { AnimatedNumber } from "../components/animated-number.js";
import { CanvasBarChart } from "../components/charts/canvas-bar-chart.js";
import { EmptyNote } from "../components/empty-note.js";
import { Overdrive } from "../components/overdrive.js";
import { Rack } from "../components/rack.js";
import { ReviewRow, ReviewRowSkeleton } from "../components/review-row.js";
import { SeverityBadge } from "../components/severity-badge.js";
import { StaggerBody, StaggerContainer, StaggerItem, StaggerRow } from "../components/stagger.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.js";
import { Table, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { useTheme } from "../hooks/use-theme.js";
import { formatCost, formatDate, formatDuration, repoName } from "../lib/format.js";
import { cssVar } from "../lib/utils.js";

const SEVERITY_LABEL: Record<string, string> = {
  critical: "kritisch",
  high: "hoch",
  medium: "mittel",
  low: "niedrig",
};

/* Brighter fills than the pill ink — bars need luminance, labels need contrast.
 * Read as CSS custom properties (not baked-in hex) so they swap with the
 * light/dark theme; canvas fillStyle can't consume var() directly. */
function severityFill(): Record<string, string> {
  return {
    critical: cssVar("--chart-sev-critical"),
    high: cssVar("--chart-sev-high"),
    medium: cssVar("--chart-sev-medium"),
    low: cssVar("--chart-sev-low"),
  };
}

/* Same severity inks as the badges/chips (SeverityBadge) — reused so the Findings
 * tally always agrees with the pills below it, not a separately invented hue. */
function severityInk(): Record<string, string> {
  return {
    critical: cssVar("--sev-critical"),
    high: cssVar("--sev-high"),
    medium: cssVar("--sev-medium"),
    low: cssVar("--sev-low"),
  };
}

/** "2026-07-07" → "7.7." — axis ticks stay short enough to sample densely. */
function formatDayShort(day: string) {
  const [, m, d] = day.split("-");
  return `${Number(d)}.${Number(m)}.`;
}

/** "2026-07-07" → "07.07.2026" — tooltips get the unabbreviated date. */
function formatDayLong(day: string) {
  const [y, m, d] = day.split("-");
  return `${d}.${m}.${y}`;
}

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/** Sentinel for "no repo filter" — a Select needs a value, and "" is not one. */
const ALL_PROJECTS = "all";

/** Axis tick for a bucket start. A week reads as its Monday, a month as its name. */
function bucketLabelShort(start: string, bucket: SpendBucket) {
  if (bucket === "month") return MONTHS[Number(start.split("-")[1]) - 1] ?? start;
  return formatDayShort(start);
}

/** Tooltip label: says which bucket it is, not just where it starts. */
function bucketLabelLong(start: string, bucket: SpendBucket) {
  const [y, m] = start.split("-");
  if (bucket === "month") return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
  if (bucket === "week") return `Woche ab ${formatDayLong(start)}`;
  return formatDayLong(start);
}

/** One figure in the stat band. `ink` drives both the icon wash and the figure — the
 * neutral default (Ink) for plain counts, a semantic ink for the two that carry meaning
 * (Findings takes the worst severity present; Beobachtungen stays Quiet Gray, since it's
 * deliberately the muted, uncounted channel — still ≥4.5:1 on white). */
function Tally({
  label,
  value,
  format,
  hint,
  icon: Icon,
  ink = "var(--foreground)",
}: {
  label: string;
  value: number;
  format?: (n: number) => string;
  hint?: string;
  icon: LucideIcon;
  ink?: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-start gap-3 px-5 py-4">
      <span
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: `color-mix(in srgb, ${ink} 12%, var(--background))`, color: ink }}
      >
        <Icon className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-[1.7rem] font-extrabold leading-none tracking-tight tabular-nums" style={{ color: ink }}>
          <AnimatedNumber value={value} format={format} />
        </span>
        <span className="h-4 text-xs text-muted-foreground">{hint}</span>
      </div>
    </div>
  );
}

export function Dashboard() {
  const [days, setDays] = useState(30);
  const [bucket, setBucket] = useState<SpendBucket>("day");
  const [spendProject, setSpendProject] = useState(ALL_PROJECTS);
  // Subscribed only so the chart/tally colors below — read from CSS custom
  // properties, since canvas fillStyle can't consume var() — recompute
  // whenever the theme toggles, not just on the next unrelated re-render.
  useTheme();
  const { data, isLoading, error } = useQuery({
    queryKey: ["stats", days],
    queryFn: () => api.stats(days),
  });
  // The board itself: the latest strips, live ones first.
  const recent = useQuery({ queryKey: ["reviews", "recent"], queryFn: () => api.reviews({}) });
  // Own query per (window, granularity, repo): toggling the chart must not refetch
  // the whole overview, and the repo filter belongs to the chart alone.
  const spendSeries = useQuery({
    queryKey: ["spend", days, bucket, spendProject],
    queryFn: () =>
      api.spend({
        days,
        bucket,
        projectId: spendProject === ALL_PROJECTS ? undefined : spendProject,
      }),
  });
  const projectList = useQuery({ queryKey: ["projects"], queryFn: () => api.projects() });

  if (error) {
    return (
      <div className="tile p-6 text-sm text-muted-foreground">
        Statistiken konnten nicht geladen werden. Seite neu laden — wenn das nicht hilft, läuft
        das Backend nicht.
      </div>
    );
  }

  const totals = data?.totals ?? {
    reviews: 0,
    findings: 0,
    observations: 0,
    costUsd: 0,
    avgCostUsd: 0,
    avgDurationS: 0,
  };
  const spend = data?.spend ?? { costUsd: 0 };
  // The backend groups by severity without an order; sort by how severe it is,
  // not by whatever order Postgres happened to return, so critical always reads
  // first regardless of which bucket has the highest count.
  const findingsBySeverity = [...(data?.findingsBySeverity ?? [])].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  );
  // Findings tally takes its color from the worst severity actually present —
  // it's the same ink as that severity's badge, never a hue invented for the stat band.
  const worstSeverity = findingsBySeverity.find((s) => s.count > 0)?.severity;
  const findingsInk = (worstSeverity && severityInk()[worstSeverity]) ?? cssVar("--foreground");

  const LIVE_STATES = ["running", "queued"];
  const boardStrips = [...(recent.data ?? [])]
    .sort((a, b) => {
      const liveDiff = Number(LIVE_STATES.includes(b.state)) - Number(LIVE_STATES.includes(a.state));
      return liveDiff !== 0 ? liveDiff : b.createdAt.localeCompare(a.createdAt);
    })
    .slice(0, 7);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold tracking-tight">Übersicht</h1>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 Tage</SelectItem>
            <SelectItem value="30">30 Tage</SelectItem>
            <SelectItem value="90">90 Tage</SelectItem>
            {/* Ohne ein Jahr hätte die Monatsansicht des Kosten-Charts drei Balken. */}
            <SelectItem value="365">1 Jahr</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Who is moving fastest right now — raw MR activity, independent of reviews. */}
      <Overdrive velocity={data?.velocity} isLoading={isLoading} />

      {/* The tally strip: one sheet of paper, six ruled boxes. */}
      <div className="tile grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        <Tally label="Reviews" value={totals.reviews} icon={GitPullRequestArrow} />
        <Tally label="Findings" value={totals.findings} icon={AlertTriangle} ink={findingsInk} />
        <Tally label="Beobachtungen" value={totals.observations} hint="ohne Regel" icon={Eye} ink="var(--muted-foreground)" />
        {/* Bezahlt wurde auch für Reviews, die unterwegs gestorben sind — deshalb
            spend und nicht totals. */}
        <Tally label="Kosten" value={spend.costUsd} format={(v) => formatCost(v)} icon={Banknote} />
        <Tally label="Ø Kosten" value={totals.avgCostUsd} hint="pro Review" format={(v) => formatCost(v)} icon={Banknote} />
        <Tally label="Ø Dauer" value={totals.avgDurationS} format={(v) => formatDuration(Math.round(v))} icon={Timer} />
      </div>

      {/* The latest reviews as listing rows, live ones on top. */}
      <Rack label="Aktuelle Reviews" count={boardStrips.length} trayClassName="space-y-2">
        {recent.isLoading ? (
          <ReviewRowSkeleton count={4} />
        ) : boardStrips.length === 0 ? (
          <EmptyNote>
            Noch keine Reviews — sie erscheinen hier, sobald in einem aktiven Projekt der erste
            Merge Request geöffnet wird.
          </EmptyNote>
        ) : (
          <StaggerContainer className="space-y-2">
            {boardStrips.map((review) => (
              <StaggerItem key={review.id}>
                <ReviewRow review={review} time={formatDate(review.createdAt)} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}
      </Rack>

      <Rack label="Reviews pro Tag">
        <div className="tile p-3">
          {isLoading ? (
            <div className="h-[220px] animate-pulse rounded-md bg-muted" />
          ) : (
            <CanvasBarChart
              height={220}
              layout="vertical"
              emptyLabel="Noch keine Reviews im gewählten Zeitraum."
              // Full magenta is reserved for today; past days wear the tint.
              data={(data?.reviewsPerDay ?? []).map((d, i, arr) => ({
                key: d.day,
                value: d.count,
                label: formatDayShort(d.day),
                color: i === arr.length - 1 ? cssVar("--chart-today") : cssVar("--chart-past"),
              }))}
              formatTooltip={(d) => `${formatDayLong(d.key)}: ${d.value} Review${d.value === 1 ? "" : "s"}`}
            />
          )}
        </div>
      </Rack>

      {/* Was wann bezahlt wurde — dieselbe Definition wie die Kosten-Kachel oben
          (fehlgeschlagene Läufe zählen mit), nur über die Zeit aufgetragen. */}
      <Rack
        label="Kosten"
        actions={
          <>
            <Select value={bucket} onValueChange={(v) => setBucket(v as SpendBucket)}>
              <SelectTrigger className="w-32" aria-label="Granularität">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">pro Tag</SelectItem>
                <SelectItem value="week">pro Woche</SelectItem>
                <SelectItem value="month">pro Monat</SelectItem>
              </SelectContent>
            </Select>
            <Select value={spendProject} onValueChange={setSpendProject}>
              <SelectTrigger className="w-44" aria-label="Repository">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PROJECTS}>alle Repos</SelectItem>
                {(projectList.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {repoName(p.pathWithNamespace)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      >
        <div className="tile p-3">
          {spendSeries.isLoading ? (
            <div className="h-[220px] animate-pulse rounded-md bg-muted" />
          ) : (
            <CanvasBarChart
              height={220}
              layout="vertical"
              emptyLabel="Keine Kosten im gewählten Zeitraum."
              // Same reading as above: the running bucket is magenta, the closed ones the tint.
              data={(spendSeries.data?.buckets ?? []).map((b, i, arr) => ({
                key: b.start,
                value: b.costUsd,
                label: bucketLabelShort(b.start, bucket),
                color: i === arr.length - 1 ? cssVar("--chart-today") : cssVar("--chart-past"),
              }))}
              formatTooltip={(d) => {
                const entry = spendSeries.data?.buckets.find((b) => b.start === d.key);
                const reviews = entry?.reviews ?? 0;
                return `${bucketLabelLong(d.key, bucket)}: ${formatCost(d.value)} · ${reviews} Review${reviews === 1 ? "" : "s"}`;
              }}
            />
          )}
        </div>
      </Rack>

      <div className="grid gap-4 lg:grid-cols-2">
        <Rack label="Findings nach Severity">
          <div className="tile h-full p-3">
            {!data || findingsBySeverity.length === 0 ? (
              <EmptyNote>Keine Findings im gewählten Zeitraum.</EmptyNote>
            ) : (
              <>
                <CanvasBarChart
                  height={140}
                  layout="horizontal"
                  data={findingsBySeverity.map((s) => ({
                    key: s.severity,
                    value: s.count,
                    label: SEVERITY_LABEL[s.severity] ?? s.severity,
                    color: severityFill()[s.severity] ?? cssVar("--muted-foreground"),
                  }))}
                  formatTooltip={(d) => `${d.label}: ${d.value} Finding${d.value === 1 ? "" : "s"}`}
                />
                <div className="mt-3 flex flex-wrap gap-4 border-t border-border pt-3">
                  {findingsBySeverity.map((s) => (
                    <div key={s.severity} className="flex items-center gap-1.5 text-sm">
                      <SeverityBadge severity={s.severity} />
                      <span className="font-mono font-bold tabular-nums">
                        <AnimatedNumber value={s.count} />
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Rack>

        <Rack label="Welche Findings überwiegen">
          <div className="tile h-full p-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Regel-ID</TableHead>
                  <TableHead className="text-right">Anzahl</TableHead>
                </TableRow>
              </TableHeader>
              {!data || data.topRules.length === 0 ? (
                <TableEmpty colSpan={2}>Keine Regel schlägt im Zeitraum aus.</TableEmpty>
              ) : (
                <StaggerBody>
                  {data.topRules.map((rule) => (
                    <StaggerRow key={rule.ruleId}>
                      <TableCell className="font-mono">{rule.ruleId}</TableCell>
                      <TableCell className="text-right font-mono font-bold tabular-nums">
                        <AnimatedNumber value={rule.count} />
                      </TableCell>
                    </StaggerRow>
                  ))}
                </StaggerBody>
              )}
            </Table>
          </div>
        </Rack>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Rack label="Projekte mit den meisten Findings">
          <div className="tile h-full p-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Projekt</TableHead>
                  <TableHead className="text-right">Anzahl</TableHead>
                </TableRow>
              </TableHeader>
              {!data || data.topProjects.length === 0 ? (
                <TableEmpty colSpan={2}>Kein Projekt sticht im Zeitraum hervor.</TableEmpty>
              ) : (
                <StaggerBody>
                  {data.topProjects.map((p) => (
                    <StaggerRow key={p.projectPath}>
                      <TableCell>{repoName(p.projectPath)}</TableCell>
                      <TableCell className="text-right font-mono font-bold tabular-nums">
                        <AnimatedNumber value={p.count} />
                      </TableCell>
                    </StaggerRow>
                  ))}
                </StaggerBody>
              )}
            </Table>
          </div>
        </Rack>

        <Rack
          label="Regelvorschläge"
          hint="Aus Beobachtungen ohne Regel — Kandidaten für das Rules-Repo. Die Zahl ist, wie oft der Agent den Vorschlag gemacht hat."
        >
          <div className="tile h-full p-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vorschlag</TableHead>
                  <TableHead className="text-right">Anzahl</TableHead>
                </TableRow>
              </TableHeader>
              {!data || data.missingRules.length === 0 ? (
                <TableEmpty colSpan={2}>Keine Beobachtungen ohne Regel.</TableEmpty>
              ) : (
                <StaggerBody>
                  {data.missingRules.map((m) => (
                    <StaggerRow key={m.suggestedRule}>
                      <TableCell>{m.suggestedRule}</TableCell>
                      <TableCell className="text-right font-mono font-bold tabular-nums">
                        <AnimatedNumber value={m.count} />
                      </TableCell>
                    </StaggerRow>
                  ))}
                </StaggerBody>
              )}
            </Table>
          </div>
        </Rack>
      </div>
    </div>
  );
}
